var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc heatmap chart
 */
import { createRef } from '@byted/light-render';
import { Group, Rect } from '../../element';
import { createRichText } from '../../rich';
import { LegendType } from '../../interface/legend';
import { SeriesModel } from '../../model';
import { registerChart } from '../index';
import { getRectLabelPosition } from '../../utils/textLayout';
var HeatMapSeriesModel = /** @class */ (function (_super) {
    __extends(HeatMapSeriesModel, _super);
    function HeatMapSeriesModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'heatmap';
        _this.legendType = LegendType.SERIES_LEGEND;
        _this.legendIcon = 'rect';
        return _this;
    }
    HeatMapSeriesModel.prototype.getDefaultOption = function (option) {
        var _a, _b;
        return __assign(__assign({}, _super.prototype.getDefaultOption.call(this, option)), { name: '', type: option.type, encode: {
                x: '',
                y: '',
                color: [(option.encode || {}).value, '#EEF1FF-#4261F6'],
                name: "" + (((_a = option.encode) === null || _a === void 0 ? void 0 : _a.name) || ((_b = option.encode) === null || _b === void 0 ? void 0 : _b.y)),
            }, coord: 'cartesian2d', borderSpacing: 0, polar: 0, xAxis: 0, yAxis: 0, label: {
                show: false,
                rotate: null,
                position: 'inside',
                distance: 4,
                formatter: function (point) { return point.value; },
                color: null,
                fontSize: 12,
            }, shapeStyle: {
                stroke: '#ffffff',
                lineWidth: 1,
            }, hover: {
                shapeStyle: {
                    stroke: '#1A1E22',
                    lineWidth: 1,
                },
                label: {
                    fontSize: null,
                    color: null,
                },
            }, animate: null, showLegend: true, visible: true, chunkMode: 'sequential', chunkSize: 1000, clip: true });
    };
    HeatMapSeriesModel.prototype.init = function () {
        this._hoverGroup = new Group({
            key: this.getKey('hover-group'),
            pointerEvents: 'none',
        });
        this._hoverPoint = new Rect({
            display: false,
            key: this.getKey('hover-point'),
        });
    };
    HeatMapSeriesModel.prototype.getInitialState = function () {
        return {
            needAnimation: this.needAnimation(),
            points: [],
            itemWidth: 0,
            itemHeight: 0,
        };
    };
    HeatMapSeriesModel.prototype.beforeRender = function () {
        var _this = this;
        this._hoverGroup.clear();
        this._hoverPoint.setAttr({ display: false });
        if (!this.visible()) {
            return;
        }
        var coordModle = this.chart.getComponentModel('coord');
        var coord = coordModle.getCoordinate(this.option);
        var dataset = this.getDataset();
        var points = dataset.mapData(function (record, dataIndex) {
            var encodeData = _this._encode.getEncodeData(record, dataIndex);
            var plotCenter = coord.dataToPoint(encodeData, true);
            var value = record[_this.option.encode.value];
            return {
                encode: encodeData,
                plotX: plotCenter.x,
                plotY: plotCenter.y,
                name: encodeData.name,
                value: value,
                color: encodeData.color,
                visible: !_this.invisibleItems.has(dataIndex),
                shapeRef: createRef(),
                labelRef: createRef(),
            };
        });
        var width;
        var height;
        if (coord.coordinateSystem === 'cartesian2d') {
            width = Math.abs(1 / coord.getXAxis().getPixelValue());
            height = Math.abs(1 / coord.getYAxis().getPixelValue());
        }
        else if (coord.coordinateSystem === 'calendar') {
            var size = coord.getCellSize();
            width = size[0];
            height = size[1];
        }
        this.state.points = points;
        this.state.itemWidth = width;
        this.state.itemHeight = height;
        this.clipPath = coordModle.getCoordClipPath(this.option, 1);
    };
    HeatMapSeriesModel.prototype.toggleHighlightPoint = function (dataIndex, active, multiple) {
        var _a, _b;
        var _c = this.option, label = _c.label, hoverLabelStyle = _c.hover.label;
        var point = this.state.points[dataIndex];
        var rect = this._hoverPoint;
        if (multiple) {
            if (!point._hoverRect) {
                point._hoverRect = new Rect();
            }
            active && this._hoverGroup.add(point._hoverRect);
            rect = point._hoverRect;
        }
        rect.setAttr(__assign(__assign(__assign(__assign(__assign({ display: active }, this.getPointShape(point)), { fill: active ? this.getHoverColor(point.color) : point.color }), this.getShapeStyle(this.option.shapeStyle)), (active ? this.getShapeStyle(this.option.hover.shapeStyle) : null)), { pointerEvents: 'none' }));
        (_b = (_a = point.labelRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.setAttr(__assign(__assign({}, this.getTextStyle(label)), (active ? this.getTextStyle(hoverLabelStyle) : null)));
    };
    HeatMapSeriesModel.prototype.renderHeatMapItems = function () {
        var _this = this;
        var group = new Group(__assign({ key: this.getKey('heatmap-item'), clip: this.option.clip && this.clipPath }, this.getShapeStyle(this.option.shapeStyle)));
        // const { itemWidth: width, itemHeight: height } = this.state;
        var shapeList = [];
        var needAnimation = this.needAnimation();
        this.state.points.map(function (point, dataIndex) {
            var plotX = point.plotX, plotY = point.plotY, encode = point.encode;
            var shapeAttr = _this.getPointShape(point);
            var barItem = new Rect(__assign(__assign({ key: encode.x + "-" + encode.y + "-" + encode.name, ref: point.shapeRef }, shapeAttr), { cursor: 'pointer', fill: encode.color, opacity: _this.visible() && point.visible ? 1 : 0, pointerEvents: _this.visible() && point.visible ? 'auto' : 'none', onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: point.encode.x,
                        point: _this.getWrappedPoint(dataIndex),
                        hasAxis: false,
                    });
                }, onMounted: needAnimation
                    ? function () {
                        barItem
                            .setAttr({
                            x: plotX,
                            y: plotY,
                            width: 0,
                            height: 0,
                        })
                            .animateTo(shapeAttr);
                    }
                    : null }));
            shapeList.push(barItem);
        });
        if (shapeList.length < this.option.chunkLimit) {
            group.addAll(shapeList);
        }
        else {
            group.replaceChunks(this.getChunks(shapeList));
        }
        return group;
    };
    HeatMapSeriesModel.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('heatmap-label'),
            clip: this.option.clip && this.clipPath,
        });
        var points = this.state.points;
        var _a = this.option.label, show = _a.show, position = _a.position, distance = _a.distance, color = _a.color;
        var _b = this.state, itemWidth = _b.itemWidth, itemHeight = _b.itemHeight;
        var width = itemWidth;
        var height = itemHeight;
        var inside = position.indexOf('inside') !== -1;
        points.map(function (point, dataIndex) {
            var plotX = point.plotX, plotY = point.plotY, visible = point.visible, labelRef = point.labelRef;
            var textPosition = getRectLabelPosition({
                top: plotY - height / 2,
                left: plotX - width / 2,
                right: plotX + width / 2,
                bottom: plotY + height / 2,
            }, position, distance);
            group.add(createRichText(__assign(__assign(__assign({ ref: labelRef, display: show && _this.visible() && visible, text: _this.getDataLabel(dataIndex) }, textPosition), { fill: inside ? '#ffffff' : color, stroke: inside ? color : '#ffffff', lineWidth: 2, pointerEvents: inside ? 'none' : 'auto' }), _this.getTextStyle(_this.option.label))));
        });
        return group;
    };
    HeatMapSeriesModel.prototype.getPointShape = function (point) {
        var borderSpacing = this.option.borderSpacing;
        var plotX = point.plotX, plotY = point.plotY;
        var _a = this.state, width = _a.itemWidth, height = _a.itemHeight;
        width -= borderSpacing;
        height -= borderSpacing;
        return {
            x: plotX - (width + 1) / 2,
            y: plotY - (height + 1) / 2,
            width: width + 1,
            height: height + 1,
            r: this.option.shapeStyle.borderRadius,
        };
    };
    HeatMapSeriesModel.prototype.render = function () {
        var group = new Group({
            key: this.getKey('line'),
        });
        group.add(this.renderHeatMapItems());
        group.add(this._hoverPoint);
        group.add(this._hoverGroup);
        if (this.option.label.show) {
            this.chart.addToTopLevel(this.renderLabels());
        }
        return group;
    };
    HeatMapSeriesModel.dependencies = ['coord', 'legend'];
    return HeatMapSeriesModel;
}(SeriesModel));
export default HeatMapSeriesModel;
registerChart('heatmap', HeatMapSeriesModel);
